"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Calendar, ArrowRight, ShieldCheck, CircleCheck as CheckCircle2, Mail, Phone, Clock, MessageSquare, UserCheck } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const benefits = [{
  id: 1,
  title: "100% Direct Consultant Access",
  description: "You work directly with me—Yassine Essaadi. No account managers or junior delegates."
}, {
  id: 2,
  title: "Bespoke SEO Growth Plan",
  description: "Tailored search strategy aligned specifically with your revenue and business goals."
}, {
  id: 3,
  title: "Zero Obligation or Pressure",
  description: "An honest, open conversation about your search opportunities and technical roadblocks."
}];
export default function Consultation() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="consultationcta" data-nav="light" className="relative bg-background py-16 md:py-24 overflow-hidden">
      {}
      <div className="aria-hidden:true pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-accent/15 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-primary/10 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-5xl mx-auto">
          <motion.div className="bg-card border border-border rounded-2xl p-6 sm:p-10 lg:p-12 shadow-xl relative overflow-hidden" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-50px"
        }}>
            {}
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-primary via-accent to-primary" />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
              {}
              <div className="lg:col-span-7 space-y-6">
                <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className="border-primary/30 text-primary font-medium px-3 py-1">
                    <ShieldCheck className="w-3.5 h-3.5 text-primary mr-1" />
                    Direct Consultant Partnership
                  </Badge>
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Clock className="w-3 h-3 text-muted-foreground" />
                    30-Minute Free Strategy Call
                  </span>
                </motion.div>

                <motion.div variants={itemVariants} className="space-y-3">
                  <h2 className="text-xl sm:text-2xl lg:text-3xl font-semibold tracking-tight text-foreground font-default">
                    Ready for Sustainable Search Growth? Let&apos;s Talk Strategy.
                  </h2>
                  <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
                    Book a free SEO consultation or request a custom audit. I will review your site&apos;s current visibility, technical baseline, and keyword potential before we speak so our conversation is purely actionable.
                  </p>
                </motion.div>

                {}
                <motion.div variants={itemVariants} className="bg-muted/60 border border-border/80 rounded-xl p-4 sm:p-5 flex items-start sm:items-center gap-4">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_consultant.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_consultant.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_consultant.webp-wvc-sizes" alt="Yassine Essaadi - Freelance SEO Consultant" className="w-14 h-14 sm:w-16 sm:h-16 rounded-full object-cover border-2 border-primary/20 shrink-0" loading="lazy" />
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-foreground text-sm sm:text-base font-default">
                        Yassine Essaadi
                      </span>
                      <span className="text-xs text-muted-foreground bg-background px-2 py-0.5 rounded-md border border-border">
                        Independent Consultant
                      </span>
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-snug">
                      &quot;When you hire YE Consultancy, you work directly with me from day one. I personally craft and execute your SEO roadmap.&quot;
                    </p>
                  </div>
                </motion.div>

                {}
                <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2">
                  <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold shadow-md px-6 py-6 rounded-lg transition-[color,background-color,box-shadow]">
                    <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                      <SafeHydrate><Calendar className="w-5 h-5 shrink-0" /></SafeHydrate>
                      <span>Book a Free SEO Consultation</span>
                      <ArrowRight className="w-4 h-4 shrink-0" />
                    </Link>
                  </Button>

                  <Button asChild variant="outline" size="lg" className="border-primary/30 text-foreground hover:bg-muted font-medium px-5 py-6 rounded-lg transition-[color,background-color,border-color]">
                    <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                      <MessageSquare className="w-4 h-4 text-primary shrink-0" />
                      <span>Get a Free Local SEO Audit</span>
                    </Link>
                  </Button>
                </motion.div>

                {}
                <motion.div variants={itemVariants} className="pt-2 flex flex-wrap items-center gap-y-2 gap-x-6 text-xs sm:text-sm text-muted-foreground">
                  <a href="mailto:contact@ye-consultancy.com" className="inline-flex items-center gap-1.5 hover:text-primary transition-colors">
                    <Mail className="w-4 h-4 text-primary" />
                    <span>contact@ye-consultancy.com</span>
                  </a>
                  <a href="tel:+447490984430" className="inline-flex items-center gap-1.5 hover:text-primary transition-colors">
                    <Phone className="w-4 h-4 text-primary" />
                    <span>+44 7490984430</span>
                  </a>
                </motion.div>
              </div>

              {}
              <div className="lg:col-span-5 bg-background border border-border/80 rounded-xl p-6 sm:p-8 space-y-6 shadow-sm">
                <div className="space-y-1">
                  <h3 className="text-lg font-semibold text-foreground font-default">
                    What to Expect
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    A straight-talking approach to search engine growth.
                  </p>
                </div>

                <Separator />

                <div className="space-y-5">
                  {benefits.map((benefit, i) => <div key={benefit.id} data-index={i} className="flex items-start gap-3">
                      <div className="p-1 rounded-full bg-accent/20 text-accent-foreground shrink-0 mt-0.5">
                        <CheckCircle2 className="w-4 h-4 text-accent" />
                      </div>
                      <div className="space-y-1">
                        <h4 className="text-sm font-semibold text-foreground font-default">
                          {benefit.title}
                        </h4>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          {benefit.description}
                        </p>
                      </div>
                    </div>)}
                </div>

                <Separator />

                <div className="bg-muted/40 rounded-lg p-3 text-center">
                  <p className="text-xs text-muted-foreground flex items-center justify-center gap-1.5">
                    <UserCheck className="w-3.5 h-3.5 text-primary" />
                    Based in Swindon, UK. Serving national &amp; international clients.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}